/**
 * AuthManager — wraps Supabase auth. Single source of truth for "who is
 * signed in right now" and the event stream when that changes.
 *
 * Construct once, call `init()` once, share the instance everywhere that
 * needs user context (the preset store, the UI header, the browser panel).
 */

import type { SupabaseClient, Session, User } from '@supabase/supabase-js';

export type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'signed-in'; user: User };

export type AuthListener = (state: AuthState) => void;

export interface AuthResult {
  /** undefined on success; message on failure. */
  readonly error?: string;
  /** For signUp when email confirmation is required. */
  readonly confirmationRequired?: boolean;
}

export class AuthManager {
  private state: AuthState = { kind: 'loading' };
  private readonly listeners = new Set<AuthListener>();
  private unsubscribeSupabase: (() => void) | null = null;

  constructor(private readonly client: SupabaseClient) {}

  /**
   * Read the persisted session (if any) and subscribe to Supabase's own
   * auth events so signInWithPassword from another tab, for example,
   * propagates into our state.
   */
  async init(): Promise<void> {
    const { data } = await this.client.auth.getSession();
    this.applySession(data.session);

    const { data: sub } = this.client.auth.onAuthStateChange((_event, session) => {
      this.applySession(session);
    });
    this.unsubscribeSupabase = () => sub.subscription.unsubscribe();
  }

  dispose(): void {
    this.unsubscribeSupabase?.();
    this.unsubscribeSupabase = null;
    this.listeners.clear();
  }

  get currentState(): AuthState {
    return this.state;
  }

  get isSignedIn(): boolean {
    return this.state.kind === 'signed-in';
  }

  get currentUser(): User | null {
    return this.state.kind === 'signed-in' ? this.state.user : null;
  }

  subscribe(listener: AuthListener): () => void {
    this.listeners.add(listener);
    // Replay current state immediately so late subscribers don't miss it.
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    const { error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return {};
  }

  async signUp(email: string, password: string): Promise<AuthResult> {
    const { data, error } = await this.client.auth.signUp({ email, password });
    if (error) return { error: error.message };
    // If email confirmation is required (default in new Supabase projects),
    // signUp returns a user but no session — the session only appears after
    // the user clicks the confirmation link.
    const confirmationRequired = !data.session && data.user !== null;
    return { confirmationRequired };
  }

  async signOut(): Promise<AuthResult> {
    const { error } = await this.client.auth.signOut();
    if (error) return { error: error.message };
    return {};
  }

  private applySession(session: Session | null): void {
    const next: AuthState = session
      ? { kind: 'signed-in', user: session.user }
      : { kind: 'signed-out' };
    if (
      this.state.kind === next.kind &&
      (this.state.kind !== 'signed-in' ||
        (next.kind === 'signed-in' && this.state.user.id === next.user.id))
    ) {
      return;
    }
    this.state = next;
    for (const fn of this.listeners) fn(this.state);
  }
}
